import React, { useState, useEffect, useMemo } from 'react';
import { MatchHighlights, HighlightWithPlayer } from '../../types/highlights.types';
import FeaturedHighlight from '../match/FeaturedHighlight';
import HighlightCard from '../match/HighlightCard';
import HighlightPlayer from '../match/HighlightPlayer';
import highlightsService from '../../services/highlightsService';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import '../../styles/components/highlights.scss';
import '../../styles/components/profile-page.scss';

interface PlayerHighlightsSectionProps {
  steamId: string;
  playerName?: string;
  playerAvatar?: string;
}

const PlayerHighlightsSection: React.FC<PlayerHighlightsSectionProps> = ({
  steamId,
  playerName,
  playerAvatar,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [highlightsData, setHighlightsData] = useState<MatchHighlights | null>(null);
  const [loading, setLoading] = useState(false);
  const [playingHighlight, setPlayingHighlight] = useState<HighlightWithPlayer | null>(null);
  const [selectedFeatured, setSelectedFeatured] = useState<HighlightWithPlayer | null>(null);

  // Fetch highlights when expanded
  useEffect(() => {
    if (isExpanded && !highlightsData && steamId) {
      const fetchHighlights = async () => {
        setLoading(true);
        const data = await highlightsService.getPlayerHighlights(steamId);
        if (data) {
          setHighlightsData(data);
        }
        setLoading(false);
      };
      fetchHighlights();
    }
  }, [isExpanded, steamId, highlightsData]);

  // Enrich highlights with player data
  const enrichedHighlights: HighlightWithPlayer[] = useMemo(() => {
    if (!highlightsData) return [];
    return highlightsData.highlights.map((highlight) => ({
      ...highlight,
      playerName: playerName,
      playerAvatar: playerAvatar,
    }));
  }, [highlightsData, playerName, playerAvatar]);

  // Find initial featured highlight (highest priority tag)
  const initialFeatured = useMemo(() => {
    const priorityOrder = ['Ace', 'FourK', 'ThreeK', 'TwoK', 'OneK'];

    for (const tag of priorityOrder) {
      const found = enrichedHighlights.find(h => h.tags.includes(tag));
      if (found) return found;
    }

    return enrichedHighlights[0];
  }, [enrichedHighlights]);

  // Use selected featured or default to initial
  const featuredHighlight = selectedFeatured || initialFeatured;

  // Other highlights (excluding featured)
  const otherHighlights = useMemo(() => {
    return enrichedHighlights.filter(h => h.highlightId !== featuredHighlight?.highlightId);
  }, [enrichedHighlights, featuredHighlight]);

  // Handlers
  const handlePlayHighlight = (highlight: HighlightWithPlayer) => {
    setPlayingHighlight(highlight);
  };

  const handleSelectFeatured = (highlight: HighlightWithPlayer) => {
    setSelectedFeatured(highlight);
  };

  const handleClosePlayer = () => {
    setPlayingHighlight(null);
  };

  const handleNextHighlight = () => {
    if (!playingHighlight) return;
    const currentIndex = enrichedHighlights.findIndex(
      (h) => h.highlightId === playingHighlight.highlightId
    );
    if (currentIndex < enrichedHighlights.length - 1) {
      setPlayingHighlight(enrichedHighlights[currentIndex + 1]);
    }
  };

  const handlePreviousHighlight = () => {
    if (!playingHighlight) return;
    const currentIndex = enrichedHighlights.findIndex(
      (h) => h.highlightId === playingHighlight.highlightId
    );
    if (currentIndex > 0) {
      setPlayingHighlight(enrichedHighlights[currentIndex - 1]);
    }
  };

  const handleDownload = (highlight: HighlightWithPlayer) => {
    const filename = `${playerName || 'highlight'}-round${highlight.roundNumber}-${highlight.killCount}k.mp4`;
    highlightsService.downloadClip(highlight.clipUrl, filename);
  };

  const handleShare = () => {
    // Share functionality can be added later if needed
  };

  const getCurrentHighlightIndex = () => {
    if (!playingHighlight) return -1;
    return enrichedHighlights.findIndex(
      (h) => h.highlightId === playingHighlight.highlightId
    );
  };

  const currentIndex = getCurrentHighlightIndex();
  const hasNext = currentIndex >= 0 && currentIndex < enrichedHighlights.length - 1;
  const hasPrevious = currentIndex > 0;

  // Don't render if no highlights
  if (!highlightsData && !loading && isExpanded) {
    return null;
  }

  return (
    <div className="player-highlights-section">
      <div
        className="player-highlights-header"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsExpanded(!isExpanded); }}
      >
        <h3 className="player-highlights-title">
          Player Highlights
          <button className="player-highlights-toggle">
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {highlightsData && (
            <span className="player-highlights-count">
              {' '}· {highlightsData.highlightCount} Clips
            </span>
          )}
        </h3>
      </div>

      {isExpanded && (
        <div className="player-highlights-content">
          {loading ? (
            <div className="player-highlights-loading">
              <div className="loading-spinner"></div>
              <p>Loading highlights...</p>
            </div>
          ) : highlightsData && highlightsData.highlightCount > 0 && featuredHighlight ? (
            <div className="highlights-section__layout">
              {/* Featured Highlight */}
              <div className="highlights-section__featured">
                <FeaturedHighlight
                  highlight={featuredHighlight}
                  onPlay={handlePlayHighlight}
                  onDownload={handleDownload}
                  onShare={handleShare}
                />
              </div>

              {/* Highlights List */}
              {otherHighlights.length > 0 && (
                <div className="highlights-section__list">
                  {otherHighlights.map((highlight) => (
                    <HighlightCard
                      key={highlight.highlightId}
                      highlight={highlight}
                      variant="list"
                      onPlay={handleSelectFeatured}
                      onDownload={handleDownload}
                      onShare={handleShare}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="player-highlights-empty">
              <p>No highlights available for this player yet.</p>
            </div>
          )}
        </div>
      )}

      {playingHighlight && (
        <HighlightPlayer
          highlight={playingHighlight}
          onClose={handleClosePlayer}
          onNext={hasNext ? handleNextHighlight : undefined}
          onPrevious={hasPrevious ? handlePreviousHighlight : undefined}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
        />
      )}
    </div>
  );
};

export default PlayerHighlightsSection;
